/**
 * raw-sender-modal.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { DialogRef, ModalComponent } from "angular2-modal";
import { BSModalContext } from "angular2-modal/plugins/bootstrap";
import { Observable } from "rxjs/Rx";

import { AuthService } from "../auth/auth.service";
import { Message, MessageConstructor, ReplyVerifyingMessage, Tagged }
  from "../server/messages/general/";
import { MessagingService } from "../server/messaging.service";
import { OUTGOING_MESSAGE_MAP }
  from "../server/validation/incoming-message-map";
import { StorageService } from "../support/APIs/storage.service";

const DEFAULT_MESSAGE : string = "{\n\"tag\": 1\n}";

interface SavedMessage
{
  name : string;
  message : string;
}
interface SavedMessageList
{
  [name : string] : string;
}

@Component({
  template: `
  <div class="modal-header">
    <button
      type="button"
      class="close"
      (click)="dialog.dismiss()"
      aria-label="Close">
      <span aria-hidden="true">&times;</span>
    </button>
    <h4 class="modal-title">
      Raw message tool &emsp;
      <span class="badge">
        {{ready ? "connected" : "DISCONNECTED"}}
        &mdash;
        {{auth.authenticated ? "logged in" : "GUEST"}}
      </span>
    </h4>
  </div>

  <div class="modal-body">
    <form [formGroup]="form">
      <div>
        <label>Enter message JSON: (ID will automatically be assigned)</label>
        <button
          id="sendButton"
          class="btn btn-primary pull-right"
          (click)="trySend()"
          [disabled]="!ready">
          Parse &amp; Send
        </button>
      </div>

      <textarea
        class="form-control"
        rows="8"
        formControlName="rawMessage">
      </textarea>
      <br><!-- #lazy #this-is-the-debug-menu #quit-harshing-my-mellow -->

      <div class="col-sm-3">
        <div class="form-group">
          <label>Create from tag type:</label>
          <select
            class="form-control"
            [formControl]="tagSelector">
            <option *ngFor="let msg of msgClasses" [value]="msg.tag">
              {{msg.name}}
            </option>
          </select>
        </div>
      </div>

      <!-- save to local storage -->
      <div class="col-sm-3">
        <div class="form-group">
          <label>Save message locally as:</label>
          <div class="input-group">
            <input
              type="text"
              class="form-control"
              formControlName="messageSaveName">
            <span class="input-group-btn">
              <button
                class="btn btn-default"
                (click)="saveMessage()">
                Save
              </button>
            </span>
          </div>
        </div>
      </div>

      <!-- load from local storage -->
      <div class="col-sm-3">
        <div class="form-group">
          <label>Load message:</label>
          <div class="input-group">
            <select class="form-control" formControlName="messageLoadName">
              <option *ngFor="let loadable of savedMessages">
                {{loadable.name}}
              </option>
            </select>
            <span class="input-group-btn">
              <button
                class="btn btn-default"
                (click)="loadMessage()">
                Load
              </button>
            </span>
          </div>
        </div>
      </div>

      <div class="col-sm-3">
        <div class="form-group">
          <button
            class="form-control btn btn-warning align-with-labels"
            (click)="eraseAllSaved()">
            Erase All
          </button>
        </div>
      </div>

      <div>
        Response should appear below. "Nonconversational" messages will not,
        but if they aren't being received by a service, they should
        appear in the console.
      </div>
    </form>
  </div>

  <div class="modal-body">
    <div class="row">
      <div class="col-sm-6">
        <label>Request</label>
        <prettyjson [obj]="request"></prettyjson>
      </div>
      <div class="col-sm-6">
        <label>Response</label>
        <prettyjson [obj]="response"></prettyjson>
      </div>
    </div>
  </div>

  <div class="modal-footer">
    <button
      type="button"
      class="btn btn-primary"
      (click)="dialog.close()">
      Close
    </button>
  </div>
  `,
  styles: [
    "/deep/ prettyjson pre {outline: none !important;}",
    "/deep/ #sendButton {position: relative; top: -8px;}"
  ]
})
export class RawSenderModalComponent
  implements ModalComponent<BSModalContext>, OnInit
{
  /**
   * Construct the {@link RawSenderModalComponent} dialog
   * @param  {MessagingService}          messaging DI
   * @param  {AuthService}               auth      DI
   * @param  {StorageService}            storage   DI
   * @param  {DialogRef<BSModalContext>} dialog    DI
   * @return {RawSenderModalComponent}
   */
  constructor (
    protected messaging : MessagingService,
    protected auth : AuthService,
    public storage : StorageService,
    public dialog : DialogRef<BSModalContext>
  ) { /* DI */ }

  public ngOnInit () : void
  {
    this.messaging.ready.subscribe((ready : boolean) => this.ready = ready);
    // TODO: move JSON validation into custom control validator!!
    this.form = new FormGroup({
      rawMessage: new FormControl(DEFAULT_MESSAGE),
      messageSaveName: new FormControl(),
      messageLoadName: new FormControl()
    });
    this.updateSavedMessages();
    this.tagSelector.valueChanges.subscribe((tag : number) =>
      this.form.get("rawMessage").setValue(`{\n\t"tag": ${tag}\n}`));
  }

  public msgClasses : MessageConstructor[] =
    Array.from(OUTGOING_MESSAGE_MAP.values());

  public tagSelector : FormControl = new FormControl();

  /**
   * The form holding the dialog's controls
   * @type {FormGroup}
   */
  public form : FormGroup;

  /**
   * The request {@link Message} created from the user's input
   * @type {Message}
   */
  public request : Message | null = null;

  /**
   * The response content to display to the user; it may be a valid
   * {@link Message} or text about errors, disconnects, or malformed content.
   * @type {any}
   */
  public response : any = null;

  /**
   * Snapshot of the {@link MessagingService}'s readiness
   * @type {boolean}
   */
  public ready : boolean;

  /**
   * Attempt to parse the user's input and send it as a {@link Message}.
   */
  public trySend () : void
  {
    const message : string = this.form.get("rawMessage").value;
    try
    {
      const objectForm : any = JSON.parse(message);
      if (typeof objectForm !== "object")
      {
        throw new Error("Outermost level of message must be object!");
      }
      if (!this.isTagged(objectForm))
      {
        throw new Error("Message must have a (numeric) 'tag' property!");
      }
      // Make the object message-like and send it
      this.send(this.castToMessage(objectForm));
    }
    catch (e)
    {
      // We threw an exception, almost certainly due to a parsing failure, so
      // alert the user (this is debug-only, we don't need no fancy stuff!) and
      // bail on the send attempt.
      console.error(e);
      alert("Could not parse your message content! See console for details.");
    }
  }

  /**
   * Send a {@link Message} object and display the request/response pair
   * @param {Message} message
   */
  private send (message : Message) : void
  {
    this.response = null;
    this.request = message;
    // since we are using allowUnboundedReplies, we will not receive any replies
    // directly through the send observable
    this.messaging.send(message).subscribe(
      (reply : Message) => this.response = reply,
      (err : any) => this.response = {META: "Server encountered an error"}
    );
  }

  /**
   * Determine if the object has a valid tag
   * @param  {any} m a potential Tagged entity
   * @return {boolean}
   */
  public isTagged (m : any) : m is Tagged
  {
    return typeof m === "object" && "tag" in m && typeof m.tag === "number";
  }

  /**
   * Assign ID and expectsReply properties to a {@link Tagged} entity so it
   * can be sent via the {@link MessagingService}.
   * @param  {Tagged}  protoMessage tagged object to be mutated
   * @return {Message}
   */
  public castToMessage (protoMessage : Tagged) : Message
  {
    const msg : Message = new ReplyVerifyingMessage();
    Object.assign(msg, {id : this.messaging.idAllocator++}, protoMessage);
    msg.meta.allowUnboundedReplies = true;
    return msg;
  }

  /**
   * The messages read from local storage.
   * @type {SavedMessage[]}
   */
  public savedMessages : SavedMessage[] = [];

  /**
   * Introduce changes to the saved message list
   * @param {SavedMessage[]} added   array of messages to insert or replace
   * @param {number[]}       removed indices of messages to splice
   */
  private updateSavedMessages (
    added : SavedMessage[] = [],
    removed : number[] = []
  ) : void
  {
    this.loadMessageList();
    added.forEach((msg : SavedMessage) => {
      const i : number = this.savedMessages.findIndex(
          (compare : SavedMessage) => msg.name === compare.name
      );
      if (i < 0)
      {
        this.savedMessages.push(msg);
      }
      else
      {
        this.savedMessages[i] = msg;
      }
    });
    if (removed.length !== 0)
    {
      throw new Error("Deletion is not yet implemented");
    }
    this.saveMessageList();
  }

  /**
   * Read the messages from storage into our class property. NOTE: This
   * transforms them from an associative object to an array for easier display.
   */
  private loadMessageList () : void
  {
    const list : SavedMessageList = this.storage.get("raw-messages");
    this.savedMessages = [];
    if (list && typeof list === "object")
    {
      for (const name in list)
      {
        if (list.hasOwnProperty(name))
        {
          this.savedMessages.push({name, message: list[name]});
        }
      }
    }

    if (this.savedMessages.length > 0)
    {
      // FormControls unfortunately don't detect the change in the select's
      // options so we set the first entry as the initially selected value.
      this.form.get("messageLoadName").setValue(this.savedMessages[0].name);
    }
    else
    {
      this.form.get("messageLoadName").reset();
    }
  }

  /**
   * Save the local message list to browser storage (see transform note above).
   */
  private saveMessageList () : void
  {
    const list : SavedMessageList = this.savedMessages.reduce(
      (prev : SavedMessageList, curr : SavedMessage) => {
        prev[curr.name] = curr.message;
        return prev;
      },
      {}
    );
    this.storage.set("raw-messages", list);
  }

  /**
   * Save the current user-entered message to the name provided.
   */
  public saveMessage () : void
  {
    const msgName : string = this.form.get("messageSaveName").value;
    const msgContent : string = this.form.get("rawMessage").value;
    this.updateSavedMessages([{name: msgName, message: msgContent}]);
  }

  /**
   * Load a message the user selected, by its name.
   */
  public loadMessage () : void
  {
    const name : string = this.form.get("messageLoadName").value;
    const target : SavedMessage = this.savedMessages.find(
      (compare : SavedMessage) => compare.name === name
    );
    if (!target)
    {
      throw new Error("Could not find message '" + name + "' in storage");
    }
    this.form.get("rawMessage").setValue(target.message);
    this.form.get("messageSaveName").setValue(target.name);
  }

  /**
   * Clear all of the messages stored in the browser's cache.
   * TODO: We could have finer-grained control here for deleting single entries,
   * but this is a debug feature, so take what you can get. :P
   */
  public eraseAllSaved () : void
  {
    this.storage.remove("raw-messages");
    this.savedMessages = [];
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/debug/raw-sender-modal.component.ts